// Filtrado, orden, paginación y métricas de la agenda de prospectos — lógica PURA, sin React.
//
// TODO se resuelve en memoria: `GET /prospects?index=seller` no acepta filtros, devuelve la agenda
// completa del vendedor ordenada por el GSI de próximo contacto.
import { mxDay, mxDayOffset } from '@/lib/dateHelpers';
import {
    prospectPhone,
    prospectReasonText,
    type ProspectBucket,
} from '@/domains/sales/wizard/prospects';
import type { PendingReason, ProspectStatus } from '@/domains/sales/wizard/types';
import { statusLabel } from './prospectsFormat';
import type { ProspectRow } from './prospectsRows';

export interface ProspectsFilters {
    /** Texto libre: nombre del cliente, folio o teléfono. */
    query: string;
    /** Chips multi-selección de la agenda. Un prospecto resuelto no cae en ninguno. */
    urgency: ProspectBucket[];
    /** Cadena vacía = «Todos». */
    status: ProspectStatus | '';
    reason: PendingReason | '';
    /** Marca del catálogo. */
    brand: string;
    /** Rango de próximo contacto, en días de calendario `YYYY-MM-DD`. */
    from: string;
    to: string;
}

export const EMPTY_FILTERS: ProspectsFilters = {
    query: '',
    urgency: [],
    status: '',
    reason: '',
    brand: '',
    from: '',
    to: '',
};

export type SortKey =
    | 'createdAt'
    | 'folio'
    | 'customerName'
    | 'product'
    | 'reason'
    | 'nextContactAt'
    | 'status';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
    key: SortKey;
    direction: SortDirection;
}

/**
 * Lo más nuevo arriba. Con la agenda por defecto (`nextContactAt` ascendente) el prospecto recién
 * creado caía al fondo y el vendedor no lo encontraba; la agenda sigue a un clic en su columna.
 */
export const DEFAULT_SORT: SortState = { key: 'createdAt', direction: 'desc' };

/** El mockup pagina de 30 en 30. */
export const PAGE_SIZE = 30;

/** Filtros en AND sobre la agenda ya traída. */
export function filterProspects(rows: ProspectRow[], filters: ProspectsFilters): ProspectRow[] {
    const query = filters.query.trim().toLowerCase();
    return rows.filter((row) => {
        const prospect = row.prospect;
        if (query) {
            const phone = prospectPhone(prospect) ?? '';
            const haystack = `${row.customerName} ${row.folio} ${phone}`.toLowerCase();
            if (!haystack.includes(query)) return false;
        }
        if (filters.status && prospect.status !== filters.status) return false;
        if (filters.reason && prospect.pendingReason !== filters.reason) return false;
        if (filters.brand && row.brand !== filters.brand) return false;
        if (filters.urgency.length > 0) {
            if (row.bucket === null || !filters.urgency.includes(row.bucket)) return false;
        }
        // Por DÍA de calendario del piso de venta: `nextContactAt` es un instante completo, y el día
        // UTC no siempre es el que la tabla pinta.
        if (filters.from || filters.to) {
            const day = mxDay(prospect.nextContactAt);
            if (filters.from && day < filters.from) return false;
            if (filters.to && day > filters.to) return false;
        }
        return true;
    });
}

/** Se ordena por lo que el vendedor LEE, no por el código interno del silo. */
function sortValue(row: ProspectRow, key: SortKey): string | number {
    switch (key) {
        case 'createdAt':
            return row.prospect.createdAt;
        case 'folio':
            return row.folio;
        case 'customerName':
            return row.customerName;
        case 'product':
            return row.productLabel;
        case 'reason':
            return prospectReasonText(row.prospect);
        case 'nextContactAt':
            return row.prospect.nextContactAt;
        case 'status':
            return statusLabel(row.prospect.status);
    }
}

export function sortProspects(rows: ProspectRow[], sort: SortState): ProspectRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = sortValue(a, sort.key);
        const vb = sortValue(b, sort.key);
        if (typeof va === 'string' && typeof vb === 'string') {
            return sign * va.localeCompare(vb, 'es-MX');
        }
        return sign * (Number(va) - Number(vb));
    });
}

/** Repetir columna invierte; cambiar de columna arranca ascendente, como el mockup. */
export function nextSort(current: SortState, key: SortKey): SortState {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: 'asc' };
}

/** Cuántos filtros hay puestos — el contador del encabezado del panel. */
export function countActiveFilters(filters: ProspectsFilters): number {
    const fields = [
        filters.query.trim(),
        filters.status,
        filters.reason,
        filters.brand,
        filters.from,
        filters.to,
    ];
    return filters.urgency.length + fields.filter((value) => value !== '').length;
}

/** Agrega o quita un valor de un grupo de chips multi-selección. */
export function toggleInList<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/** `YYYY-MM-DD` de dentro de `days` días en el piso de venta — los atajos Hoy/3d/7d. */
export function dayAhead(days: number): string {
    return mxDayOffset(-days);
}

/** Las marcas presentes en la agenda, en alfabético — el select de producto de interés. */
export function brandOptions(rows: ProspectRow[]): string[] {
    const brands = new Set<string>();
    for (const row of rows) if (row.brand) brands.add(row.brand);
    return [...brands].sort((a, b) => a.localeCompare(b, 'es-MX'));
}

export interface ProspectMetrics {
    overdue: number;
    today: number;
    /** Ventas completadas sobre el total, redondeado a entero. */
    conversionPct: number;
    lost: number;
}

/**
 * Las cuatro métricas de la toolbar, SIEMPRE sobre el universo completo y no sobre lo filtrado:
 * son la foto de la agenda del vendedor, y filtrar por «Perdidos» dejaría la conversión en 0%.
 */
export function prospectMetrics(rows: ProspectRow[]): ProspectMetrics {
    let overdue = 0;
    let today = 0;
    let closed = 0;
    let lost = 0;
    for (const row of rows) {
        if (row.bucket === 'overdue') overdue += 1;
        if (row.bucket === 'today') today += 1;
        if (row.prospect.status === 'CLOSED') closed += 1;
        if (row.prospect.status === 'LOST') lost += 1;
    }
    return {
        overdue,
        today,
        conversionPct: rows.length > 0 ? Math.round((closed / rows.length) * 100) : 0,
        lost,
    };
}
